import { useQuery } from "react-query";
import { chunk } from "lodash";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { z } from "zod";
import { isQueryReady } from "src/api/axiosClient/utils";
import i18next from "i18next";

export const getSomeSsqApiKey = "getSomeSqqs";
const maxSsqIdsPerRequest = 30;

const SimpleSsqSchema = z.object({
    id: z.number(),
    title: z.string(),
    displayTitle: z.string().nullable().optional(),
});

const SsqCollectionSchema = z.object({
    "hydra:member": z.array(SimpleSsqSchema),
});

export type SimpleSsq = z.infer<typeof SimpleSsqSchema>;

type Options = {
    enabled?: boolean;
    allowMissing?: boolean;
};

export const useGetSomeSsq = (ssqIds: number[] | undefined, options?: Options) => {
    const { enabled = true, allowMissing = false } = options || {};
    const canonicalSsqIds = [...new Set(ssqIds ?? [])].sort((left, right) => left - right);
    const locale = i18next.resolvedLanguage || "en";
    const isEnabled = enabled && canonicalSsqIds.length > 0;
    const query = useQuery({
        cacheTime: 300000, // Cache for 5 minutes instead of 0
        staleTime: 180000, // Consider data fresh for 3 minutes
        enabled: isEnabled,
        keepPreviousData: false,
        queryKey: [getSomeSsqApiKey, canonicalSsqIds, locale],
        queryFn: async () => {
            if (canonicalSsqIds.length === 0) {
                return [];
            }

            const batches = chunk(canonicalSsqIds, maxSsqIdsPerRequest);
            const collections = await Promise.all(
                batches.map(async (ids) => {
                    const response = await sureApiCall.get("/api/ssqs", {
                        params: { id: ids, page: 1, itemsPerPage: ids.length },
                        headers: { "Accept-Language": locale },
                    });
                    return zodParseWithLog(response.data, SsqCollectionSchema, getSomeSsqApiKey)["hydra:member"];
                }),
            );

            return collections.flat().sort((left, right) => left.id - right.id);
        },
        select: (ssqs) => {
            const ssqsById = new Map(ssqs.map((ssq) => [ssq.id, ssq] as const));
            if (!allowMissing) {
                const missingId = (ssqIds ?? []).find((id) => !ssqsById.has(id));
                if (missingId !== undefined) {
                    throw new Error(`SSQ collection is missing requested ID ${missingId}`);
                }
            }

            return (ssqIds ?? []).flatMap((id) => {
                const ssq = ssqsById.get(id);
                return ssq ? [ssq] : [];
            });
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        ssqs: query.data,
        isReady,
        ...query,
    };
};
